import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './contexts/AuthContext'
import { BrandingProvider } from './contexts/BrandingContext'
import { ToastProvider } from './contexts/ToastContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { LoadingScreen } from './components/ui'
import {
  RequireAuth, RequireAdmin, RequireClient, RequireGuest, RequireModule, RequirePlatformAdmin,
} from './components/common/ProtectedRoute'
import AuthLayout from './components/layout/AuthLayout'
import {
  InfraAdminLayout, InfraClientLayout,
  AutomationsLayout, EngageLayout, HubLayout, PlatformLayout,
} from './components/layout/ModuleLayouts'
import ImpersonationBanner from './components/common/ImpersonationBanner'
import PublicLayout from './components/layout/PublicLayout'

// ── Auth pages ────────────────────────────────────────────────────────────────
import LoginPage from './pages/auth/LoginPage'
import { RegisterPage, ForgotPasswordPage, ResetPasswordPage, TwoFactorPage } from './pages/auth/AuthPages'

// ── Dashboard pages ───────────────────────────────────────────────────────────
import InfraAdminDashboard   from './pages/infra/admin/DashboardPage'
import EngageDashboard       from './pages/engage/DashboardPage'
import AutomationsDashboard  from './pages/automations/DashboardPage'
import HubMarketplace        from './pages/hub/MarketplacePage'

// ── Lazy loaded pages ─────────────────────────────────────────────────────────
const L = (imp) => lazy(imp)

// Infra Admin
const ClientListPage     = L(() => import('./pages/infra/admin/ClientsPage'))
const ProjectListPage    = L(() => import('./pages/infra/admin/ProjectsPage'))
const EnvironmentList    = L(() => import('./pages/infra/admin/EnvironmentsPage'))
const MaintenancePage    = L(() => import('./pages/infra/admin/MaintenancePage'))
const HealthPage         = L(() => import('./pages/infra/admin/HealthPage'))
const TicketListPage     = L(() => import('./pages/infra/admin/TicketsPage'))
const TicketDetailPage   = L(() => import('./pages/infra/admin/TicketDetailPage'))
const BillingAdminPage   = L(() => import('./pages/infra/admin/BillingPage'))
const InvoicesPage       = L(() => import('./pages/infra/admin/InvoicesPage'))
const SslPage            = L(() => import('./pages/infra/admin/SslPage'))
const SlaPage            = L(() => import('./pages/infra/admin/SlaPage'))
const RunbooksPage       = L(() => import('./pages/infra/admin/RunbooksPage'))
const BackupsPage        = L(() => import('./pages/infra/admin/BackupsPage'))
const ReportsAdminPage   = L(() => import('./pages/infra/admin/ReportsPage'))
const UsersPage          = L(() => import('./pages/infra/admin/UsersPage'))
const SettingsPage       = L(() => import('./pages/infra/admin/SettingsPage'))
const PlatformCredentialsPage = L(() => import('./pages/infra/admin/PlatformCredentialsPage'))
const IntegrationsPage   = L(() => import('./pages/infra/admin/IntegrationsPage'))
const NotifRulesPage     = L(() => import('./pages/infra/admin/NotificationRulesPage'))
const ContentPage        = L(() => import('./pages/infra/admin/ContentPage'))
const CustomTasksPage    = L(() => import('./pages/infra/admin/CustomTasksPage'))
const ServiceReqAdmin    = L(() => import('./pages/infra/admin/ServiceRequestsPage'))
const CannedRespPage     = L(() => import('./pages/infra/admin/CannedResponsesPage'))
const ContactSubmissionsPage = L(() => import('./pages/infra/admin/ContactSubmissionsPage'))
const AdminClientDetail  = L(() => import('./pages/infra/admin/ClientDetailPage'))
const AdminProjectDetail = L(() => import('./pages/infra/admin/ProjectDetailPage'))
const AdminEnvDetail     = L(() => import('./pages/infra/admin/EnvironmentDetailPage'))
const AdminInvoiceDetail = L(() => import('./pages/infra/admin/InvoiceDetailPage'))
const DomainListingsPage = L(() => import('./pages/infra/admin/DomainListingsPage'))

// Infra Client
const ClientDashboard    = L(() => import('./pages/infra/client/DashboardPage'))
const ClientProjectsPage = L(() => import('./pages/infra/client/ProjectsPage'))
const ClientTicketsPage  = L(() => import('./pages/infra/client/TicketsPage'))
const ClientBillingPage  = L(() => import('./pages/infra/client/BillingPage'))
const ClientReportsPage  = L(() => import('./pages/infra/client/ReportsPage'))
const ClientMaintPage    = L(() => import('./pages/infra/client/MaintenancePage'))
const ClientServiceReq   = L(() => import('./pages/infra/client/ServiceRequestsPage'))
const ClientTeamPage     = L(() => import('./pages/infra/client/TeamPage'))
const ClientApiTokens    = L(() => import('./pages/infra/client/ApiTokensPage'))
const ClientNotifs       = L(() => import('./pages/infra/client/NotificationsPage'))
const HelpCenterPage     = L(() => import('./pages/infra/client/HelpCenterPage'))
const AgencyBillingPage  = L(() => import('./pages/infra/client/AgencyBillingPage'))
const RetainerPage       = L(() => import('./pages/infra/client/upgrades/RetainerPage'))
const ClientProjectDetail = L(() => import('./pages/infra/client/ProjectDetailPage'))
const ClientTicketDetail  = L(() => import('./pages/infra/client/TicketDetailPage'))

// Automations
const AutoProjectsPage   = L(() => import('./pages/automations/ProjectsPage'))
const AutoProjectDetail  = L(() => import('./pages/automations/ProjectDetailPage'))
const DocumentsPage      = L(() => import('./pages/automations/DocumentsPage'))
const SpecificationsPage = L(() => import('./pages/automations/SpecificationsPage'))
const TestPlansPage      = L(() => import('./pages/automations/TestPlansPage'))
const TestPlanExecution  = L(() => import('./pages/automations/TestPlanExecutionPage'))
const DocumentDetailPage = L(() => import('./pages/automations/DocumentDetailPage'))
const SpecDetailPage     = L(() => import('./pages/automations/SpecificationDetailPage'))
const TestPlanDetailPage = L(() => import('./pages/automations/TestPlanDetailPage'))
const AutoSearchPage      = L(() => import('./pages/automations/SearchPage'))
const AiUsagePage         = L(() => import('./pages/automations/AiUsagePage'))
const CodeAnalysisPage    = L(() => import('./pages/automations/CodeAnalysisPage'))
const VersionTimelinePage = L(() => import('./pages/automations/VersionTimelinePage'))
const ChangelogPage       = L(() => import('./pages/automations/ChangelogPage'))
const AutoBillingPage    = L(() => import('./pages/automations/BillingPage'))
const AutoSettingsPage   = L(() => import('./pages/automations/SettingsPage'))
const AutoNotifPrefs     = L(() => import('./pages/automations/NotificationPrefsPage'))
const AutoIntegrations   = L(() => import('./pages/automations/IntegrationsPage'))
const AutoNotifLog       = L(() => import('./pages/automations/NotificationLogPage'))

// Engage
const ContactsPage       = L(() => import('./pages/engage/ContactsPage'))
const ContactDetail      = L(() => import('./pages/engage/ContactDetailPage'))
const DomainDetail       = L(() => import('./pages/engage/DomainDetailPage'))
const SubscriberDetail   = L(() => import('./pages/engage/SubscriberDetailPage'))
const CampaignsPage      = L(() => import('./pages/engage/CampaignsPage'))
const CampaignDetail     = L(() => import('./pages/engage/CampaignDetailPage'))
const WorkflowsPage      = L(() => import('./pages/engage/WorkflowsPage'))
const WorkflowBuilder    = L(() => import('./pages/engage/WorkflowBuilderPage'))
const SequencesPage      = L(() => import('./pages/engage/SequencesPage'))
const AnalyticsPage      = L(() => import('./pages/engage/AnalyticsPage'))
const DomainsPage        = L(() => import('./pages/engage/DomainsPage'))
const TemplatesPage      = L(() => import('./pages/engage/TemplatesPage'))
const SubscribersPage    = L(() => import('./pages/engage/SubscribersPage'))
const PipelinePage       = L(() => import('./pages/engage/PipelinePage'))
const LiveChatPage       = L(() => import('./pages/engage/LiveChatPage'))
const SocialPage         = L(() => import('./pages/engage/SocialSchedulerPage'))
const ContentStudio      = L(() => import('./pages/engage/ContentStudioPage'))
const CreativeStudio     = L(() => import('./pages/engage/CreativeStudioPage'))
const MediaLibrary       = L(() => import('./pages/engage/MediaLibraryPage'))
const HashtagManager     = L(() => import('./pages/engage/HashtagManagerPage'))
const RssFeeds           = L(() => import('./pages/engage/RssFeedsPage'))
const SeoTools           = L(() => import('./pages/engage/SeoToolsPage'))
const ChatbotFlows       = L(() => import('./pages/engage/ChatbotFlowsPage'))
const AutoTagsPage       = L(() => import('./pages/engage/AutoTagsPage'))
const ApprovalsPage      = L(() => import('./pages/engage/ApprovalsPage'))
const FunnelBuilder      = L(() => import('./pages/engage/FunnelBuilderPage'))
const FunnelAnalytics    = L(() => import('./pages/engage/FunnelAnalyticsPage'))
const RoiTracking        = L(() => import('./pages/engage/RoiTrackingPage'))
const UtmAnalytics       = L(() => import('./pages/engage/UtmAnalyticsPage'))
const ClientAccounts     = L(() => import('./pages/engage/ClientAccountsPage'))
const EngageBillingPage  = L(() => import('./pages/engage/BillingPage'))
const GdprPage           = L(() => import('./pages/engage/GdprPage'))
const AuditLogPage       = L(() => import('./pages/engage/AuditLogPage'))
const EngageSettings     = L(() => import('./pages/engage/SettingsPage'))
const ShopifyPage        = L(() => import('./pages/engage/ShopifyPage'))
const WebhookLogsPage    = L(() => import('./pages/engage/WebhookLogsPage'))
const SlaTrackingPage    = L(() => import('./pages/engage/SlaTrackingPage'))

// Hub
const HubIntegrations    = L(() => import('./pages/hub/IntegrationsPage'))
const HubWebhooks        = L(() => import('./pages/hub/WebhooksPage'))
const HubApiKeys         = L(() => import('./pages/hub/ApiKeysPage'))
const HubApiDocs         = L(() => import('./pages/hub/ApiDocsPage'))
const HubApiKeyDetail    = L(() => import('./pages/hub/ApiKeyDetailPage'))
const HubIntegrationDetail = L(() => import('./pages/hub/IntegrationDetailPage'))



// Prospect Hunter
const ProspectConfigPage = L(() => import('./pages/infra/admin/ProspectConfigPage'))

// Prospect Sequences
const SequencesListPage   = L(() => import('./pages/infra/admin/prospects/SequencesPage'))
const SequenceBuilderPage = L(() => import('./pages/infra/admin/prospects/SequenceBuilderPage'))
const SequenceStatsPage   = L(() => import('./pages/infra/admin/prospects/SequenceStatsPage'))
const ProspectHunterPage = L(() => import('./pages/infra/admin/prospects/HunterPage'))
const ProspectLeadPage   = L(() => import('./pages/infra/admin/prospects/LeadPage'))
// Upgrade Services
const UpgradeCatalog      = L(() => import('./pages/infra/client/upgrades/CatalogPage'))
const UpgradeOrderPage    = L(() => import('./pages/infra/client/upgrades/OrderPage'))
const UpgradeTrackerPage  = L(() => import('./pages/infra/client/upgrades/TrackerPage'))
const UpgradeOrdersList   = L(() => import('./pages/infra/client/upgrades/OrdersPage'))
const AdminUpgradeOrders  = L(() => import('./pages/infra/admin/upgrades/OrdersPage'))
const AdminUpgradeOrderDetail = L(() => import('./pages/infra/admin/upgrades/OrderDetailPage'))
const AdminUpgradeServices    = L(() => import('./pages/infra/admin/upgrades/ServicesPage'))

// Platform Admin
const PlatformDashboard    = L(() => import('./pages/platform/DashboardPage'))
const PlatformUsers        = L(() => import('./pages/platform/UsersPage'))
const PlatformDeletions    = L(() => import('./pages/platform/AccountDeletionsPage'))
const PlatformAudit        = L(() => import('./pages/platform/AuditLogPage'))
const PlatformFlags        = L(() => import('./pages/platform/FeatureFlagsPage'))
const PlatformMaintenance  = L(() => import('./pages/platform/MaintenancePage'))
const PlatformAnnouncements= L(() => import('./pages/platform/AnnouncementsPage'))
const PlatformEmailTpl     = L(() => import('./pages/platform/EmailTemplatesPage'))
const PlatformHealth       = L(() => import('./pages/platform/HealthPage'))
const PlatformAiUsage      = L(() => import('./pages/platform/AiUsagePage'))
const PlatformDeliver      = L(() => import('./pages/platform/DeliverabilityPage'))
const PlatformIntegrations = L(() => import('./pages/platform/IntegrationsPage'))
const PlatformExports      = L(() => import('./pages/platform/ExportsPage'))
const PlatformSettings     = L(() => import('./pages/platform/SettingsPage'))

// Public / Marketing
const HomePage           = L(() => import('./pages/public/HomePage'))
const PricingPage        = L(() => import('./pages/public/PricingPage'))
const ContactPage        = L(() => import('./pages/public/ContactPage'))
const BlogPage           = L(() => import('./pages/public/BlogPage'))
const BlogPostPage       = L(() => import('./pages/public/BlogPostPage'))
const AboutPage          = L(() => import('./pages/public/AboutPage'))
const DomainsForSalePage = L(() => import('./pages/public/DomainsForSalePage'))
const PublicStatusPage   = L(() => import('./pages/public/StatusPage'))

// Shared
const ProfilePage        = L(() => import('./pages/ProfilePage'))
const UpgradePage        = L(() => import('./pages/UpgradePage'))
const NotFoundPage       = L(() => import('./pages/NotFoundPage'))

const Suspense_ = ({ children }) => (
  <Suspense fallback={<div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-[var(--color-primary)] border-t-transparent animate-spin" /></div>}>
    {children}
  </Suspense>
)

function S(Component) {
  return <Suspense_><Component /></Suspense_>
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <BrandingProvider>
        <ToastProvider>
          <AuthProvider>
            <Toaster
              position="top-right"
              toastOptions={{
                style: { background: '#071640', color: '#EEF4FF', border: '1px solid #1A2F60', fontFamily: "'DM Sans', sans-serif", fontSize: '13px' },
                success: { iconTheme: { primary: '#00E5C8', secondary: '#020818' } },
                error:   { iconTheme: { primary: '#f87171', secondary: '#020818' } },
              }}
            />
            <ImpersonationBanner />
            <Routes>
              {/* ── Public / Marketing ── */}
              <Route element={<PublicLayout />}>
                <Route path="/"                 element={S(HomePage)} />
                <Route path="/pricing"          element={S(PricingPage)} />
                <Route path="/contact"          element={S(ContactPage)} />
                <Route path="/blog"             element={S(BlogPage)} />
                <Route path="/blog/:slug"       element={S(BlogPostPage)} />
                <Route path="/about"            element={S(AboutPage)} />
                <Route path="/domains-for-sale" element={S(DomainsForSalePage)} />
              </Route>
              <Route path="/status/:token"   element={S(PublicStatusPage)} />

              {/* ── Guest / Auth ── */}
              <Route element={<RequireGuest />}>
                <Route element={<AuthLayout />}>
                  <Route path="/login"           element={<LoginPage />} />
                  <Route path="/register"        element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password"  element={<ResetPasswordPage />} />
                </Route>
              </Route>

              {/* 2FA — outside RequireGuest so partially-logged-in users can reach it */}
              <Route element={<AuthLayout />}>
                <Route path="/2fa-challenge" element={<TwoFactorPage />} />
              </Route>

              {/* ── Infra Admin ── */}
              <Route element={<RequireAdmin />}>
                <Route element={<InfraAdminLayout />}>
                  <Route path="/infra/admin"                    element={<InfraAdminDashboard />} />
                  <Route path="/infra/admin/clients"            element={S(ClientListPage)} />
                  <Route path="/infra/admin/clients/:id"        element={S(AdminClientDetail)} />
                  <Route path="/infra/admin/projects"           element={S(ProjectListPage)} />
                  <Route path="/infra/admin/projects/:id"       element={S(AdminProjectDetail)} />
                  <Route path="/infra/admin/environments"       element={S(EnvironmentList)} />
                  <Route path="/infra/admin/environments/:id"   element={S(AdminEnvDetail)} />
                  <Route path="/infra/admin/maintenance"        element={S(MaintenancePage)} />
                  <Route path="/infra/admin/health"             element={S(HealthPage)} />
                  <Route path="/infra/admin/tickets"            element={S(TicketListPage)} />
                  <Route path="/infra/admin/tickets/:id"        element={S(TicketDetailPage)} />
                  <Route path="/infra/admin/billing"            element={S(BillingAdminPage)} />
                  <Route path="/infra/admin/invoices"           element={S(InvoicesPage)} />
                  <Route path="/infra/admin/invoices/:id"       element={S(AdminInvoiceDetail)} />
                  <Route path="/infra/admin/ssl"                element={S(SslPage)} />
                  <Route path="/infra/admin/sla"                element={S(SlaPage)} />
                  <Route path="/infra/admin/runbooks"           element={S(RunbooksPage)} />
                  <Route path="/infra/admin/backups"            element={S(BackupsPage)} />
                  <Route path="/infra/admin/reports"            element={S(ReportsAdminPage)} />
                  <Route path="/infra/admin/users"              element={S(UsersPage)} />
                  <Route path="/infra/admin/settings"           element={S(SettingsPage)} />
                  <Route path="/infra/admin/platform-credentials" element={S(PlatformCredentialsPage)} />
                  <Route path="/infra/admin/integrations"       element={S(IntegrationsPage)} />
                  <Route path="/infra/admin/notification-rules" element={S(NotifRulesPage)} />
                  <Route path="/infra/admin/content"            element={S(ContentPage)} />
                  <Route path="/infra/admin/custom-tasks"       element={S(CustomTasksPage)} />
                  <Route path="/infra/admin/service-requests"   element={S(ServiceReqAdmin)} />
                  <Route path="/infra/admin/canned-responses"   element={S(CannedRespPage)} />
                  <Route path="/infra/admin/contact-submissions" element={S(ContactSubmissionsPage)} />
                  <Route path="/infra/admin/domain-listings"     element={S(DomainListingsPage)} />



                  <Route path="/infra/admin/prospects/sequences"             element={S(SequencesListPage)} />
                  <Route path="/infra/admin/prospects/sequences/new"         element={S(SequenceBuilderPage)} />
                  <Route path="/infra/admin/prospects/sequences/:id"         element={S(SequenceStatsPage)} />
                  <Route path="/infra/admin/prospects/sequences/:id/stats"   element={S(SequenceStatsPage)} />
                  <Route path="/infra/admin/prospects/sequences/:id/edit"    element={S(SequenceBuilderPage)} />
                  <Route path="/infra/admin/prospects/config"    element={S(ProspectConfigPage)} />
                  <Route path="/infra/admin/prospects"           element={S(ProspectHunterPage)} />
                  <Route path="/infra/admin/prospects/leads/:id" element={S(ProspectLeadPage)} />
                  <Route path="/infra/admin/prospects/campaigns" element={S(ProspectHunterPage)} />
                  <Route path="/infra/admin/upgrades/orders"    element={S(AdminUpgradeOrders)} />
                  <Route path="/infra/admin/upgrades/orders/:id" element={S(AdminUpgradeOrderDetail)} />
                  <Route path="/infra/admin/upgrades/services"  element={S(AdminUpgradeServices)} />
                  <Route path="/infra/admin/profile"            element={S(ProfilePage)} />
                </Route>
              </Route>

              {/* ── Infra Client ── */}
              <Route element={<RequireClient />}>
                <Route element={<InfraClientLayout />}>
                  <Route path="/infra/client"                       element={S(ClientDashboard)} />
                  <Route path="/infra/client/projects"              element={S(ClientProjectsPage)} />
                  <Route path="/infra/client/projects/:id"          element={S(ClientProjectDetail)} />
                  <Route path="/infra/client/tickets"               element={S(ClientTicketsPage)} />
                  <Route path="/infra/client/tickets/:id"           element={S(ClientTicketDetail)} />
                  <Route path="/infra/client/billing"               element={S(ClientBillingPage)} />
                  <Route path="/infra/client/reports"               element={S(ClientReportsPage)} />
                  <Route path="/infra/client/maintenance"           element={S(ClientMaintPage)} />
                  <Route path="/infra/client/service-requests"      element={S(ClientServiceReq)} />
                  <Route path="/infra/client/team"                  element={S(ClientTeamPage)} />
                  <Route path="/infra/client/api-tokens"            element={S(ClientApiTokens)} />
                  <Route path="/infra/client/notifications"         element={S(ClientNotifs)} />
                  <Route path="/infra/client/help"                  element={S(HelpCenterPage)} />
                  <Route path="/infra/client/agency/billing"        element={S(AgencyBillingPage)} />

                  <Route path="/infra/client/upgrades/retainer"     element={S(RetainerPage)} />
                  <Route path="/infra/client/upgrades"              element={S(UpgradeCatalog)} />
                  <Route path="/infra/client/upgrades/:slug"         element={S(UpgradeOrderPage)} />
                  <Route path="/infra/client/upgrades/orders"        element={S(UpgradeOrdersList)} />
                  <Route path="/infra/client/upgrades/orders/:id"    element={S(UpgradeTrackerPage)} />
                  <Route path="/infra/client/profile"               element={S(ProfilePage)} />
                </Route>
              </Route>

              {/* ── Automations ── */}
              <Route element={<RequireModule module="automations" />}>
                <Route element={<AutomationsLayout />}>
                  <Route path="/automations"                          element={<AutomationsDashboard />} />
                  <Route path="/automations/projects"                 element={S(AutoProjectsPage)} />
                  <Route path="/automations/projects/:id"             element={S(AutoProjectDetail)} />
                  <Route path="/automations/documents"                element={S(DocumentsPage)} />
                  <Route path="/automations/documents/:id"            element={S(DocumentDetailPage)} />
                  <Route path="/automations/specifications"           element={S(SpecificationsPage)} />
                  <Route path="/automations/specifications/:id"       element={S(SpecDetailPage)} />
                  <Route path="/automations/test-plans"               element={S(TestPlansPage)} />
                  <Route path="/automations/test-plans/:id"           element={S(TestPlanDetailPage)} />
                  <Route path="/automations/test-plans/:id/execute"   element={S(TestPlanExecution)} />
                  <Route path="/automations/search"                   element={S(AutoSearchPage)} />
                  <Route path="/automations/ai-usage"                element={S(AiUsagePage)} />
                  <Route path="/automations/code-analysis"           element={S(CodeAnalysisPage)} />
                  <Route path="/automations/versions"                 element={S(VersionTimelinePage)} />
                  <Route path="/automations/changelog"                element={S(ChangelogPage)} />
                  <Route path="/automations/notifications"             element={S(AutoNotifPrefs)} />
                  <Route path="/automations/notifications/log"        element={S(AutoNotifLog)} />
                  <Route path="/automations/integrations"             element={S(AutoIntegrations)} />
                  <Route path="/automations/billing"                  element={S(AutoBillingPage)} />
                  <Route path="/automations/settings"                 element={S(AutoSettingsPage)} />
                </Route>
              </Route>

              {/* ── Engage ── */}
              <Route element={<RequireModule module="engage" />}>
                <Route element={<EngageLayout />}>
                  <Route path="/engage"                         element={<EngageDashboard />} />
                  <Route path="/engage/contacts"                element={S(ContactsPage)} />
                  <Route path="/engage/contacts/:id"            element={S(ContactDetail)} />
                  <Route path="/engage/campaigns"               element={S(CampaignsPage)} />
                  <Route path="/engage/campaigns/:id"           element={S(CampaignDetail)} />
                  <Route path="/engage/workflows"               element={S(WorkflowsPage)} />
                  <Route path="/engage/workflows/new"          element={S(WorkflowBuilder)} />
                  <Route path="/engage/workflows/:id/edit"     element={S(WorkflowBuilder)} />
                  <Route path="/engage/sequences"               element={S(SequencesPage)} />
                  <Route path="/engage/analytics"               element={S(AnalyticsPage)} />
                  <Route path="/engage/utm-analytics"           element={S(UtmAnalytics)} />
                  <Route path="/engage/funnel-analytics"        element={S(FunnelAnalytics)} />
                  <Route path="/engage/roi-tracking"            element={S(RoiTracking)} />
                  <Route path="/engage/domains"                 element={S(DomainsPage)} />
                  <Route path="/engage/domains/:id"             element={S(DomainDetail)} />
                  <Route path="/engage/templates"               element={S(TemplatesPage)} />
                  <Route path="/engage/subscribers"             element={S(SubscribersPage)} />
                  <Route path="/engage/subscribers/:id"         element={S(SubscriberDetail)} />
                  <Route path="/engage/pipeline"                element={S(PipelinePage)} />
                  <Route path="/engage/live-chat"               element={S(LiveChatPage)} />
                  <Route path="/engage/social-scheduler"        element={S(SocialPage)} />
                  <Route path="/engage/content-studio"          element={S(ContentStudio)} />
                  <Route path="/engage/creative-studio"         element={S(CreativeStudio)} />
                  <Route path="/engage/media-library"           element={S(MediaLibrary)} />
                  <Route path="/engage/hashtag-manager"         element={S(HashtagManager)} />
                  <Route path="/engage/rss-feeds"               element={S(RssFeeds)} />
                  <Route path="/engage/seo-tools"               element={S(SeoTools)} />
                  <Route path="/engage/chatbot-flows"           element={S(ChatbotFlows)} />
                  <Route path="/engage/auto-tags"               element={S(AutoTagsPage)} />
                  <Route path="/engage/approvals"               element={S(ApprovalsPage)} />
                  <Route path="/engage/funnel-builder"          element={S(FunnelBuilder)} />
                  <Route path="/engage/client-accounts"         element={S(ClientAccounts)} />
                  <Route path="/engage/shopify"                 element={S(ShopifyPage)} />
                  <Route path="/engage/webhooks"                element={S(WebhookLogsPage)} />
                  <Route path="/engage/sla"                     element={S(SlaTrackingPage)} />
                  <Route path="/engage/billing"                 element={S(EngageBillingPage)} />
                  <Route path="/engage/gdpr"                    element={S(GdprPage)} />
                  <Route path="/engage/audit-log"               element={S(AuditLogPage)} />
                  <Route path="/engage/settings"                element={S(EngageSettings)} />
                  <Route path="/engage/profile"                 element={S(ProfilePage)} />
                </Route>
              </Route>

              {/* ── Hub ── */}
              <Route element={<RequireModule module="hub" />}>
                <Route element={<HubLayout />}>
                  <Route path="/hub"              element={<HubMarketplace />} />
                  <Route path="/hub/integrations" element={S(HubIntegrations)} />
                  <Route path="/hub/integrations/:id" element={S(HubIntegrationDetail)} />
                  <Route path="/hub/webhooks"     element={S(HubWebhooks)} />
                  <Route path="/hub/api-keys"     element={S(HubApiKeys)} />
                  <Route path="/hub/api-keys/:id" element={S(HubApiKeyDetail)} />
                  <Route path="/hub/api-docs"     element={S(HubApiDocs)} />
                </Route>
              </Route>

              {/* ── Platform Admin ── */}
              <Route element={<RequirePlatformAdmin />}>
                <Route element={<PlatformLayout />}>
                  <Route path="/platform"                   element={S(PlatformDashboard)} />
                  <Route path="/platform/users"             element={S(PlatformUsers)} />
                  <Route path="/platform/account-deletions" element={S(PlatformDeletions)} />
                  <Route path="/platform/audit"             element={S(PlatformAudit)} />
                  <Route path="/platform/feature-flags"     element={S(PlatformFlags)} />
                  <Route path="/platform/maintenance"       element={S(PlatformMaintenance)} />
                  <Route path="/platform/announcements"     element={S(PlatformAnnouncements)} />
                  <Route path="/platform/email-templates"   element={S(PlatformEmailTpl)} />
                  <Route path="/platform/health"            element={S(PlatformHealth)} />
                  <Route path="/platform/ai-usage"          element={S(PlatformAiUsage)} />
                  <Route path="/platform/deliverability"    element={S(PlatformDeliver)} />
                  <Route path="/platform/integrations"      element={S(PlatformIntegrations)} />
                  <Route path="/platform/exports"           element={S(PlatformExports)} />
                </Route>
              </Route>

              {/* Settings holds live secrets, so it sits behind a higher rank
                  than the rest of the platform surface. */}
              <Route element={<RequirePlatformAdmin minRank={3} />}>
                <Route element={<PlatformLayout />}>
                  <Route path="/platform/settings" element={S(PlatformSettings)} />
                </Route>
              </Route>

              {/* ── Shared ── */}
              <Route element={<RequireAuth />}>
                <Route path="/profile" element={<Suspense_><ProfilePage /></Suspense_>} />
                <Route path="/upgrade" element={<Suspense_><UpgradePage /></Suspense_>} />
              </Route>

              {/* ── 404 ── */}
              <Route path="*"  element={<Suspense_><NotFoundPage /></Suspense_>} />
            </Routes>
          </AuthProvider>
        </ToastProvider>
      </BrandingProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}
